import type { ListModelsResponse } from "@repo/marketplace-admin"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader, Stat, StatStrip } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import {
	PrepareButton,
	ToggleLiveDialog,
} from "@/features/card-scanner/model-actions"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	Instant,
	MODEL_TYPE_LABELS,
	MODEL_TYPES,
	type ModelType,
	QueryError,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/card-scanner/models")({
	component: ModelsPage,
})

type ModelRow = ListModelsResponse["models"][number]

function ModelsPage() {
	const allowed = useScannerGate()
	const navigate = useNavigate()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.listModels.queryOptions({ input: {} }),
		enabled: allowed && isAuthReady,
	})

	const [toggleTarget, setToggleTarget] = React.useState<ModelRow | null>(null)

	const columnDefs = React.useMemo<ColDef<ModelRow>[]>(
		() => [
			{
				field: "type",
				rowGroup: true,
				hide: true,
				valueFormatter: (p) =>
					MODEL_TYPE_LABELS[p.value as ModelType] ?? p.value,
			},
			{
				headerName: "Version",
				field: "version",
				sort: "desc",
				minWidth: 110,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data ? (
						<span className="inline-flex items-center gap-2">
							<span className="font-medium tabular-nums">
								v{p.data.version}
							</span>
							{p.data.live && <StatusDot tone="ok">live</StatusDot>}
						</span>
					) : null,
			},
			{
				headerName: "Coverage",
				colId: "coverage",
				minWidth: 150,
				valueGetter: (p) =>
					p.data?.coverage
						? p.data.coverage.done / Math.max(1, p.data.coverage.expected)
						: null,
				cellRenderer: (p: ICellRendererParams<ModelRow>) => {
					const coverage = p.data?.coverage
					if (!coverage) return <span className="text-muted-foreground">—</span>
					const complete = coverage.done >= coverage.expected
					return (
						<span className="inline-flex items-center gap-2">
							<span
								className={cn(
									"font-medium tabular-nums",
									complete ? "text-ok" : "text-warn",
								)}
							>
								{coverage.done}/{coverage.expected}
							</span>
							<span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
								<span
									className={cn(
										"block h-full rounded-full",
										complete ? "bg-ok" : "bg-warn",
									)}
									style={{
										width: `${Math.min(100, (coverage.done / Math.max(1, coverage.expected)) * 100)}%`,
									}}
								/>
							</span>
						</span>
					)
				},
			},
			{
				headerName: "Checksum",
				field: "checksum",
				minWidth: 170,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									data-no-row-click
									className="max-w-40 truncate font-mono text-muted-foreground hover:text-primary-text"
									onClick={() => {
										navigator.clipboard
											.writeText(p.data?.checksum ?? "")
											.then(() => toast.success("Checksum copied"))
											.catch(() => toast.error("Could not copy checksum"))
									}}
								>
									{p.data.checksum}
								</button>
							</TooltipTrigger>
							<TooltipContent className="font-mono text-xs">
								{p.data.checksum} (click to copy)
							</TooltipContent>
						</Tooltip>
					) : null,
			},
			{
				headerName: "Source job",
				field: "sourceJobId",
				minWidth: 120,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data?.sourceJobId ? (
						<span className="font-mono">{p.data.sourceJobId.slice(0, 8)}…</span>
					) : (
						<span className="text-meta">backfilled</span>
					),
			},
			{
				headerName: "Created",
				field: "createdAt",
				minWidth: 130,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data ? <Instant iso={p.data.createdAt} /> : null,
			},
			{
				headerName: "Promoted",
				colId: "promoted",
				minWidth: 150,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data?.promotedAt ? (
						<span>
							<Instant iso={p.data.promotedAt} />
							{p.data.promotedBy && (
								<span className="text-muted-foreground">
									{" "}
									· {p.data.promotedBy}
								</span>
							)}
						</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				headerName: "",
				colId: "actions",
				minWidth: 240,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<ModelRow>) =>
					p.data ? (
						<span className="inline-flex items-center gap-2" data-no-row-click>
							{p.data.coverage && !p.data.live && (
								<PrepareButton model={p.data} />
							)}
							<Button
								variant={p.data.live ? "outline" : "default"}
								size="sm"
								onClick={() => setToggleTarget(p.data ?? null)}
							>
								{p.data.live ? "Demote" : "Promote"}
							</Button>
						</span>
					) : null,
			},
		],
		[],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Models"
				count={data?.models.length}
				description="Trained scanner models; the live flag drives signature generation and the v2 manifest"
			/>

			{data && <LiveSummary models={data.models} />}

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="models" error={error} />
			) : data?.models.length === 0 ? (
				<p className="text-muted-foreground">
					No models registered. Models appear when a training job completes, or
					seed the current app-bundled versions with the backfill script.
				</p>
			) : (
				<ScannerGrid<ModelRow>
					columnDefs={columnDefs}
					rowData={data?.models}
					getRowId={(row) => row.id}
					onRowClicked={(row) =>
						navigate({
							to: "/card-scanner/models/$modelId",
							params: { modelId: row.id },
						})
					}
					gridOptions={{
						rowClass: "cursor-pointer",
						groupDisplayType: "groupRows",
						groupDefaultExpanded: -1,
						groupRowRendererParams: {
							suppressCount: true,
							innerRenderer: (p: ICellRendererParams) => {
								const type = p.node.key as ModelType
								const rows = (data?.models ?? []).filter(
									(model) => model.type === type,
								)
								const live = rows.filter((model) => model.live)
								return (
									<span className="inline-flex items-center gap-2.5">
										<span className="font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.07em]">
											{MODEL_TYPE_LABELS[type] ?? type}
										</span>
										<span className="text-meta text-xs">
											{rows.length} version{rows.length === 1 ? "" : "s"} ·{" "}
											{live.length
												? `live: ${live.map((model) => `v${model.version}`).join(", ")}`
												: "none live"}
										</span>
									</span>
								)
							},
						},
					}}
				/>
			)}

			{toggleTarget && (
				<ToggleLiveDialog
					model={toggleTarget}
					onClose={() => setToggleTarget(null)}
				/>
			)}
		</div>
	)
}

/** What is live right now, per type, as a stat strip. */
function LiveSummary({ models }: { models: ModelRow[] }) {
	const byType = MODEL_TYPES.filter((type) =>
		models.some((model) => model.type === type),
	)
	if (byType.length === 0) return null
	return (
		<StatStrip>
			{byType.map((type) => {
				const live = models.filter((model) => model.type === type && model.live)
				return (
					<Stat
						key={type}
						label={MODEL_TYPE_LABELS[type]}
						value={
							live.length
								? live.map((model) => `v${model.version}`).join(" + ")
								: "none live"
						}
						muted={live.length === 0}
					/>
				)
			})}
		</StatStrip>
	)
}
